import React, { useState, useEffect, useCallback } from 'react';
import { dealRoomsApi } from '../lib/api';
import { formatCurrency, formatDate, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Pencil, Trash, Folder, CurrencyDollar, CheckCircle, Circle,
  Lightning, Bank, ArrowsClockwise, Buildings, Briefcase, Handshake,
  Copy, Sparkle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'capital_raising', label: 'Capital Raising', icon: CurrencyDollar, color: 'bg-blue-500' },
  { id: 'financing', label: 'Financing', icon: Bank, color: 'bg-emerald-500' },
  { id: 'restructure', label: 'Restructure', icon: ArrowsClockwise, color: 'bg-amber-500' },
  { id: 'infrastructure_procurement', label: 'Infrastructure', icon: Buildings, color: 'bg-purple-500' },
  { id: 'ma', label: 'M&A', icon: Handshake, color: 'bg-red-500' },
  { id: 'ipo', label: 'IPO', icon: Lightning, color: 'bg-cyan-500' },
  { id: 'debt', label: 'Debt', icon: Briefcase, color: 'bg-zinc-600' },
];

const STATUSES = [
  { id: 'preparation', label: 'Preparation', color: 'bg-zinc-500' },
  { id: 'active', label: 'Active', color: 'bg-blue-500' },
  { id: 'due_diligence', label: 'Due Diligence', color: 'bg-amber-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-purple-500' },
  { id: 'closing', label: 'Closing', color: 'bg-emerald-500' },
  { id: 'completed', label: 'Completed', color: 'bg-green-700' },
  { id: 'withdrawn', label: 'Withdrawn', color: 'bg-red-500' },
];

export default function DealPrepLabPage() {
  const [rooms, setRooms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: 'capital_raising', status: 'preparation', description: '',
    target_amount: 0, sector: '', stakeholders: [], milestones: [], checklist: []
  });
  const [stakeInput, setStakeInput] = useState('');
  const [mileInput, setMileInput] = useState('');
  const [checkInput, setCheckInput] = useState('');

  const fetchRooms = useCallback(async () => {
    try {
      const params = {};
      if (catFilter !== 'all') params.category = catFilter;
      const res = await dealRoomsApi.getAll(params);
      setRooms(res.data);
    } catch (error) { toast.error('Failed to load deal rooms'); }
    finally { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const fetchTemplates = async () => {
    try {
      const res = await dealRoomsApi.getTemplates();
      setTemplates(res.data.templates);
      setIsTemplateOpen(true);
    } catch (error) { toast.error('Failed to load templates'); }
  };

  const applyTemplate = (tmpl) => {
    setFormData({
      name: tmpl.name, category: tmpl.category, status: 'preparation',
      description: tmpl.description, target_amount: tmpl.target_amount || 0,
      sector: '', stakeholders: [],
      milestones: (tmpl.milestones || []).map(m => ({ ...m, done: false })),
      checklist: (tmpl.checklist || []).map(c => ({ item: c, done: false }))
    });
    setIsTemplateOpen(false);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await dealRoomsApi.update(editing.id, formData);
        toast.success('Deal room updated');
      } else {
        await dealRoomsApi.create(formData);
        toast.success('Deal room created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchRooms();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal room?')) return;
    try { await dealRoomsApi.delete(id); toast.success('Deleted'); if (selectedRoom?.id === id) setSelectedRoom(null); fetchRooms(); }
    catch { toast.error('Failed'); }
  };

  const toggleChecklist = async (room, index) => {
    const updated = [...(room.checklist || [])];
    updated[index] = { ...updated[index], done: !updated[index].done };
    try {
      await dealRoomsApi.update(room.id, { checklist: updated });
      fetchRooms();
      if (selectedRoom?.id === room.id) setSelectedRoom({ ...room, checklist: updated });
    } catch { toast.error('Failed'); }
  };

  const openEdit = (room) => {
    setEditing(room);
    setFormData({
      name: room.name, category: room.category, status: room.status,
      description: room.description || '', target_amount: room.target_amount || 0,
      sector: room.sector || '', stakeholders: room.stakeholders || [],
      milestones: room.milestones || [], checklist: room.checklist || []
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ name: '', category: 'capital_raising', status: 'preparation', description: '', target_amount: 0, sector: '', stakeholders: [], milestones: [], checklist: [] });
    setStakeInput(''); setMileInput(''); setCheckInput('');
  };

  const getChecklistProgress = (checklist) => {
    if (!checklist?.length) return 0;
    return Math.round(checklist.filter(c => c.done).length / checklist.length * 100);
  };

  return (
    <div className="space-y-6" data-testid="deal-prep-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Deal Preparation Lab</h1>
          <p className="text-muted-foreground text-sm mt-1">Capital raising, financing, restructuring & procurement preparation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates} className="rounded-sm" data-testid="templates-btn">
            <Sparkle size={18} className="mr-2" /> Use Template
          </Button>
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="new-room-btn">
            <Plus size={18} className="mr-2" /> New Deal Room
          </Button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={catFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCatFilter('all')} className="rounded-full">All</Button>
        {CATEGORIES.map(cat => (
          <Button key={cat.id} variant={catFilter === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setCatFilter(cat.id)} className="rounded-full">
            {React.createElement(cat.icon, { size: 14, className: "mr-1" })} {cat.label}
          </Button>
        ))}
      </div>

      <div className="flex gap-6">
        {/* Room Cards */}
        <div className="flex-1">
          {loading ? <div className="text-center p-8 text-muted-foreground">Loading...</div> :
          rooms.length === 0 ? (
            <div className="text-center p-16 border border-dashed border-border rounded-xl">
              <Folder size={48} className="mx-auto text-muted-foreground mb-4 opacity-40" />
              <p className="text-muted-foreground font-medium">No deal rooms yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create one or use a template to get started</p>
              <div className="flex gap-3 justify-center mt-4">
                <Button variant="outline" onClick={fetchTemplates}>Browse Templates</Button>
                <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>Create Room</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {rooms.map(room => {
                const catConfig = CATEGORIES.find(c => c.id === room.category) || CATEGORIES[0];
                const Icon = catConfig.icon;
                const statusConfig = STATUSES.find(s => s.id === room.status) || STATUSES[0];
                const progress = getChecklistProgress(room.checklist);
                return (
                  <div
                    key={room.id}
                    className={`bg-card border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${selectedRoom?.id === room.id ? 'border-primary shadow-md' : 'border-border'}`}
                    onClick={() => setSelectedRoom(room)}
                    data-testid={`room-${room.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg ${catConfig.color} flex items-center justify-center`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{room.name}</h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge className={`${statusConfig.color} text-white text-xs`}>{statusConfig.label}</Badge>
                            <Badge variant="outline" className="text-xs">{catConfig.label}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(room); }}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(room.id); }}><Trash size={14} /></Button>
                      </div>
                    </div>
                    {room.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{room.description}</p>}
                    <div className="flex items-center gap-4 text-sm mb-3">
                      {room.target_amount > 0 && (
                        <span className="font-semibold">{formatCurrency(room.target_amount)}</span>
                      )}
                      <span className="text-muted-foreground">{room.document_count || 0} docs</span>
                      <span className="text-muted-foreground">{room.task_count || 0} tasks</span>
                    </div>
                    {room.checklist?.length > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Readiness</span>
                          <span>{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-1.5" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedRoom && (
          <div className="w-96 flex-shrink-0 bg-card border border-border rounded-xl p-5 sticky top-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <h2 className="font-heading text-lg font-bold mb-1">{selectedRoom.name}</h2>
            {selectedRoom.description && <p className="text-sm text-muted-foreground mb-4">{selectedRoom.description}</p>}
            
            {/* Checklist */}
            {selectedRoom.checklist?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Preparation Checklist</h3>
                <div className="space-y-2">
                  {selectedRoom.checklist.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => toggleChecklist(selectedRoom, i)}>
                      {item.done ? <CheckCircle size={18} className="text-success" weight="fill" /> : <Circle size={18} className="text-muted-foreground" />}
                      <span className={`text-sm ${item.done ? 'line-through text-muted-foreground' : ''}`}>{typeof item === 'string' ? item : item.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Milestones */}
            {selectedRoom.milestones?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Milestones</h3>
                <div className="space-y-2">
                  {selectedRoom.milestones.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className="text-sm font-medium">{m.name}</span>
                      {m.target_date && <span className="text-xs text-muted-foreground">{formatDate(m.target_date)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stakeholders */}
            {selectedRoom.stakeholders?.length > 0 && (
              <div>
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Stakeholders</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedRoom.stakeholders.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Deal Room' : 'New Deal Room'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="room-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Amount</Label>
                <Input type="number" min="0" value={formData.target_amount} onChange={(e) => setFormData({ ...formData, target_amount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select value={formData.sector || "none"} onValueChange={(v) => setFormData({ ...formData, sector: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Checklist Items</Label>
              <div className="flex gap-2">
                <Input value={checkInput} onChange={(e) => setCheckInput(e.target.value)} placeholder="Add checklist item" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (checkInput.trim()) { setFormData({ ...formData, checklist: [...formData.checklist, { item: checkInput.trim(), done: false }] }); setCheckInput(''); } } }} />
                <Button type="button" variant="outline" onClick={() => { if (checkInput.trim()) { setFormData({ ...formData, checklist: [...formData.checklist, { item: checkInput.trim(), done: false }] }); setCheckInput(''); } }}>Add</Button>
              </div>
              <div className="space-y-1">{formData.checklist.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-1.5 bg-muted/50 rounded text-sm">
                  <span>{typeof c === 'string' ? c : c.item}</span>
                  <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => setFormData({ ...formData, checklist: formData.checklist.filter((_, j) => j !== i) })}>x</button>
                </div>
              ))}</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="room-submit">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Deal Room Templates</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Ready-to-use templates with pre-built checklists and milestones</p>
          <div className="space-y-3">
            {templates.map((tmpl, i) => {
              const catConfig = CATEGORIES.find(c => c.id === tmpl.category) || CATEGORIES[0];
              const Icon = catConfig.icon;
              return (
                <div key={i} className="border border-border rounded-xl p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg ${catConfig.color} flex items-center justify-center`}>
                        <Icon size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{tmpl.name}</h3>
                        <p className="text-xs text-muted-foreground">{tmpl.description}</p>
                        {tmpl.target_amount > 0 && <p className="text-sm font-semibold mt-1">{formatCurrency(tmpl.target_amount)}</p>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => applyTemplate(tmpl)} data-testid={`use-template-${i}`}>
                      <Copy size={14} className="mr-1" /> Use
                    </Button>
                  </div>
                  <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                    <span>{tmpl.checklist?.length || 0} checklist items</span>
                    <span>{tmpl.milestones?.length || 0} milestones</span>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
